import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { Users, TrendingUp, Activity, Calendar, DollarSign, CreditCard } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Navigation } from '@/components/Navigation';
import { BackButton } from '@/components/BackButton';

interface DAUMAUData {
  date: string;
  dau: number;
  mau: number;
}

interface BlogAnalytics {
  post_id: string;
  title: string;
  views: number;
  unique_views: number;
}

interface RevenueData {
  date: string;
  revenue: number;
  transactions: number;
}

interface CoinPackage {
  amount: number;
  count: number;
  revenue: number;
}

interface PaymentMethod {
  method: string;
  count: number;
  revenue: number;
}

const COLORS = ['hsl(var(--primary))', 'hsl(var(--secondary))', 'hsl(var(--accent))', '#8884d8', '#82ca9d'];

export default function Analytics() {
  const { userRole } = useAuth();
  const [dauMauData, setDauMauData] = useState<DAUMAUData[]>([]);
  const [blogAnalytics, setBlogAnalytics] = useState<BlogAnalytics[]>([]);
  const [currentDAU, setCurrentDAU] = useState(0);
  const [currentMAU, setCurrentMAU] = useState(0);
  const [revenueData, setRevenueData] = useState<RevenueData[]>([]);
  const [coinPackages, setCoinPackages] = useState<CoinPackage[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userRole === 'admin') {
      loadAnalytics();
    }
  }, [userRole]);

  const loadAnalytics = async () => {
    setLoading(true);
    await Promise.all([loadDAUMAU(), loadBlogAnalytics(), loadPaymentAnalytics()]);
    setLoading(false);
  };

  const loadDAUMAU = async () => {
    // Get last 30 days of activity
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const { data: activityData } = await supabase
      .from('user_activity')
      .select('activity_date, user_id')
      .gte('activity_date', thirtyDaysAgo.toISOString().split('T')[0])
      .order('activity_date', { ascending: true });

    if (!activityData) return;

    // Calculate DAU for each day
    const dauByDate = new Map<string, Set<string>>();
    activityData.forEach(({ activity_date, user_id }) => {
      if (!dauByDate.has(activity_date)) {
        dauByDate.set(activity_date, new Set());
      }
      dauByDate.get(activity_date)?.add(user_id);
    });

    // Calculate MAU (unique users in last 30 days)
    const allUsers = new Set(activityData.map(d => d.user_id));
    const mau = allUsers.size;

    // Get today's DAU
    const today = new Date().toISOString().split('T')[0];
    const todayUsers = dauByDate.get(today)?.size || 0;

    setCurrentDAU(todayUsers);
    setCurrentMAU(mau);

    // Format data for chart
    const chartData: DAUMAUData[] = [];
    const last7Days = Array.from({ length: 7 }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - (6 - i));
      return date.toISOString().split('T')[0];
    });

    last7Days.forEach(date => {
      chartData.push({
        date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        dau: dauByDate.get(date)?.size || 0,
        mau: mau,
      });
    });

    setDauMauData(chartData);
  };

  const loadBlogAnalytics = async () => {
    const { data: posts } = await supabase
      .from('blog_posts')
      .select('id, title, view_count')
      .eq('is_published', true)
      .order('view_count', { ascending: false })
      .limit(10);

    if (!posts) return;

    const analytics: BlogAnalytics[] = await Promise.all(
      posts.map(async (post) => {
        const { data: views } = await supabase
          .from('blog_views')
          .select('user_id')
          .eq('post_id', post.id);

        const uniqueUsers = new Set(
          views?.filter(v => v.user_id).map(v => v.user_id)
        );

        return {
          post_id: post.id,
          title: post.title,
          views: post.view_count || 0,
          unique_views: uniqueUsers.size,
        };
      })
    );

    setBlogAnalytics(analytics);
  };

  const loadPaymentAnalytics = async () => {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const { data: transactions } = await supabase
      .from('transactions')
      .select('*')
      .eq('type', 'purchase')
      .eq('status', 'success')
      .gte('created_at', thirtyDaysAgo.toISOString())
      .order('created_at', { ascending: true });

    if (!transactions) return;

    // Calculate total revenue
    const total = transactions.reduce((sum, t) => sum + t.amount, 0);
    setTotalRevenue(total);

    // Revenue trends (last 30 days)
    const revenueByDate = new Map<string, { revenue: number; count: number }>();
    transactions.forEach(t => {
      const date = new Date(t.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const current = revenueByDate.get(date) || { revenue: 0, count: 0 };
      revenueByDate.set(date, {
        revenue: current.revenue + t.amount,
        count: current.count + 1,
      });
    });

    const revenueChart: RevenueData[] = Array.from(revenueByDate.entries()).map(([date, data]) => ({
      date,
      revenue: data.revenue,
      transactions: data.count,
    }));
    setRevenueData(revenueChart);

    // Popular coin packages
    const packageMap = new Map<number, { count: number; revenue: number }>();
    transactions.forEach(t => {
      const amount = t.amount;
      const current = packageMap.get(amount) || { count: 0, revenue: 0 };
      packageMap.set(amount, {
        count: current.count + 1,
        revenue: current.revenue + amount,
      });
    });

    const packages: CoinPackage[] = Array.from(packageMap.entries())
      .map(([amount, data]) => ({
        amount,
        count: data.count,
        revenue: data.revenue,
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
    setCoinPackages(packages);

    // Payment method statistics
    const methodMap = new Map<string, { count: number; revenue: number }>();
    transactions.forEach(t => {
      const method = (t.metadata as any)?.payment_method || 'Unknown';
      const current = methodMap.get(method) || { count: 0, revenue: 0 };
      methodMap.set(method, {
        count: current.count + 1,
        revenue: current.revenue + t.amount,
      });
    });

    const methods: PaymentMethod[] = Array.from(methodMap.entries()).map(([method, data]) => ({
      method,
      count: data.count,
      revenue: data.revenue,
    }));
    setPaymentMethods(methods);
  };

  if (userRole !== 'admin') {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card className="p-12 text-center">
          <p className="text-muted-foreground">
            You don't have permission to view analytics
          </p>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <div className="container mx-auto px-4 py-8">
        <BackButton to="/" />
        <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Analytics Dashboard</h1>
        <p className="text-muted-foreground">
          Track user engagement and content performance
        </p>
      </div>

      <Tabs defaultValue="users" className="space-y-6">
        <TabsList>
          <TabsTrigger value="users">User Activity</TabsTrigger>
          <TabsTrigger value="blog">Blog Analytics</TabsTrigger>
          <TabsTrigger value="payments">Payment Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="p-6">
              <div className="flex items-center gap-4">
                <div className="bg-primary/10 p-3 rounded-lg">
                  <Calendar className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Daily Active Users</p>
                  <p className="text-3xl font-bold">{currentDAU}</p>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-center gap-4">
                <div className="bg-primary/10 p-3 rounded-lg">
                  <Users className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Monthly Active Users</p>
                  <p className="text-3xl font-bold">{currentMAU}</p>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-center gap-4">
                <div className="bg-primary/10 p-3 rounded-lg">
                  <Activity className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">DAU/MAU Ratio</p>
                  <p className="text-3xl font-bold">
                    {currentMAU > 0 ? ((currentDAU / currentMAU) * 100).toFixed(1) : 0}%
                  </p>
                </div>
              </div>
            </Card>
          </div>

          <Card className="p-6">
            <h3 className="text-xl font-semibold mb-4">Last 7 Days Activity</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={dauMauData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="dau"
                  stroke="hsl(var(--primary))"
                  name="Daily Active Users"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="mau"
                  stroke="hsl(var(--secondary))"
                  name="Monthly Active Users"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </TabsContent>

        <TabsContent value="blog" className="space-y-6">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">Top Blog Posts</h3>
              <Badge variant="secondary">
                <TrendingUp className="h-4 w-4 mr-1" />
                By Views
              </Badge>
            </div>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={blogAnalytics}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="title"
                  angle={-45}
                  textAnchor="end"
                  height={120}
                  interval={0}
                  tick={{ fontSize: 12 }}
                />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar
                  dataKey="views"
                  fill="hsl(var(--primary))"
                  name="Total Views"
                />
                <Bar
                  dataKey="unique_views"
                  fill="hsl(var(--secondary))"
                  name="Unique Views"
                />
              </BarChart>
            </ResponsiveContainer>
          </Card>

          <Card className="p-6">
            <h3 className="text-xl font-semibold mb-4">Blog Post Details</h3>
            <div className="space-y-4">
              {blogAnalytics.map((post, index) => (
                <div
                  key={post.post_id}
                  className="flex items-center justify-between p-4 bg-muted rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <div className="text-2xl font-bold text-muted-foreground">
                      #{index + 1}
                    </div>
                    <div>
                      <h4 className="font-semibold">{post.title}</h4>
                      <p className="text-sm text-muted-foreground">
                        {post.unique_views} unique visitors
                      </p>
                    </div>
                  </div>
                  <Badge variant="outline" className="text-lg px-4 py-2">
                    {post.views} views
                  </Badge>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="payments" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="p-6">
              <div className="flex items-center gap-4">
                <div className="bg-primary/10 p-3 rounded-lg">
                  <DollarSign className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Revenue (30d)</p>
                  <p className="text-3xl font-bold">{totalRevenue.toLocaleString()} coins</p>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-center gap-4">
                <div className="bg-primary/10 p-3 rounded-lg">
                  <TrendingUp className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Transactions</p>
                  <p className="text-3xl font-bold">
                    {revenueData.reduce((sum, d) => sum + d.transactions, 0)}
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-center gap-4">
                <div className="bg-primary/10 p-3 rounded-lg">
                  <CreditCard className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Avg Transaction</p>
                  <p className="text-3xl font-bold">
                    {revenueData.length > 0
                      ? Math.round(totalRevenue / revenueData.reduce((sum, d) => sum + d.transactions, 0))
                      : 0}{' '}
                    coins
                  </p>
                </div>
              </div>
            </Card>
          </div>

          <Card className="p-6">
            <h3 className="text-xl font-semibold mb-4">Revenue Trends (Last 30 Days)</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip />
                <Legend />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="revenue"
                  stroke="hsl(var(--primary))"
                  name="Revenue (coins)"
                  strokeWidth={2}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="transactions"
                  stroke="hsl(var(--secondary))"
                  name="Transactions"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="p-6">
              <h3 className="text-xl font-semibold mb-4">Popular Coin Packages</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={coinPackages}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="amount" label={{ value: 'Coins', position: 'insideBottom', offset: -5 }} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="hsl(var(--primary))" name="Purchases" />
                </BarChart>
              </ResponsiveContainer>
              <div className="mt-4 space-y-2">
                {coinPackages.map((pkg, index) => (
                  <div
                    key={pkg.amount}
                    className="flex items-center justify-between p-3 bg-muted rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <Badge variant="outline">#{index + 1}</Badge>
                      <span className="font-semibold">{pkg.amount} coins</span>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">{pkg.count} purchases</p>
                      <p className="text-sm font-semibold">{pkg.revenue.toLocaleString()} coins revenue</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <Card className="p-6">
              <h3 className="text-xl font-semibold mb-4">Payment Methods</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={paymentMethods}
                    dataKey="count"
                    nameKey="method"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label
                  >
                    {paymentMethods.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-4 space-y-2">
                {paymentMethods.map((method, index) => (
                  <div
                    key={method.method}
                    className="flex items-center justify-between p-3 bg-muted rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <span className="font-semibold capitalize">{method.method}</span>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">{method.count} transactions</p>
                      <p className="text-sm font-semibold">{method.revenue.toLocaleString()} coins</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
      </div>
    </div>
  );
}
